import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navigation } from '@/components/layout/Navigation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { analyzeImage } from '@/lib/api';
import { 
  Upload, 
  Image as ImageIcon, 
  Loader2, 
  X,
  ArrowRight,
  Camera
} from 'lucide-react';

const UploadPage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [productName, setProductName] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Convert file to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  // Handle file selection
  const handleFileSelect = async (file) => {
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image size must be less than 10MB');
      return;
    }
    
    setImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  // Handle file input change
  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
  };

  // Handle drag events
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelect(file);
  }, []);

  // Clear image
  const clearImage = () => {
    setImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Submit for analysis
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!image) return;
    
    setAnalyzing(true);
    try {
      const base64 = await fileToBase64(image);
      const result = await analyzeImage(base64, productName || null);
      toast.success('Image analyzed successfully!');
      navigate(`/analysis/${result.id}`);
    } catch (error) {
      console.error('Analysis error:', error);
      toast.error(error.response?.data?.detail || 'Analysis failed. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="upload-page">
      <Navigation />
      
      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">
            Upload Label Image
          </h1>
          <p className="text-muted-foreground text-lg">
            Take a photo or upload an image of the ingredient label
          </p>
        </div>

        <Card className="border border-border">
          <CardHeader>
            <CardTitle>Image Analysis</CardTitle>
            <CardDescription>
              Our AI will extract and analyze all ingredients from the label
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Product Name */}
              <div className="space-y-2">
                <Label htmlFor="product-name">Product Name (optional)</Label>
                <Input
                  id="product-name"
                  placeholder="e.g., Organic Almond Butter"
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                  data-testid="upload-product-name-input"
                />
              </div>

              {/* Upload Zone */}
              <div className="space-y-2">
                <Label>Label Image</Label>
                
                {!imagePreview ? (
                  <div
                    className={`upload-zone rounded-xl p-8 text-center cursor-pointer ${
                      isDragging ? 'dragging' : ''
                    }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="upload-zone"
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleInputChange}
                      className="hidden"
                      data-testid="file-input"
                    />
                    
                    <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                      <Upload className="w-8 h-8 text-muted-foreground" />
                    </div>
                    
                    <h3 className="text-lg font-semibold mb-2">
                      Drop your image here
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      or click to browse your files
                    </p>
                    
                    <div className="flex justify-center gap-3">
                      <Button 
                        type="button" 
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          fileInputRef.current?.click();
                        }}
                      >
                        <ImageIcon className="w-4 h-4 mr-2" />
                        Choose File
                      </Button>
                      <Button 
                        type="button"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          // Trigger file input with camera capture
                          if (fileInputRef.current) {
                            fileInputRef.current.setAttribute('capture', 'environment');
                            fileInputRef.current.click();
                            fileInputRef.current.removeAttribute('capture');
                          }
                        }}
                      >
                        <Camera className="w-4 h-4 mr-2" />
                        Take Photo
                      </Button>
                    </div>
                    
                    <p className="text-xs text-muted-foreground mt-4">
                      Supports JPG, PNG, WebP up to 10MB
                    </p>
                  </div>
                ) : (
                  <div className="relative rounded-xl overflow-hidden border border-border">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full max-h-[400px] object-contain bg-muted"
                      data-testid="image-preview"
                    />
                    <button
                      type="button"
                      onClick={clearImage}
                      className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors"
                      data-testid="clear-image-btn"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full"
                disabled={!image || analyzing}
                data-testid="analyze-image-btn"
              >
                {analyzing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analyzing image...
                  </>
                ) : (
                  <>
                    Analyze Label
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Tips */}
        <div className="mt-8 p-6 bg-muted/50 rounded-xl">
          <h3 className="font-semibold mb-3">Tips for best results</h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs flex-shrink-0 mt-0.5">1</span>
              <span>Ensure the ingredient list is clearly visible and in focus</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs flex-shrink-0 mt-0.5">2</span>
              <span>Good lighting helps - avoid shadows on the label</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs flex-shrink-0 mt-0.5">3</span>
              <span>Include the entire ingredient section in the photo</span>
            </li>
          </ul>
        </div>
      </main>
    </div>
  );
};

export default UploadPage;
